/**
 * Express checkout: the arithmetic and the shape-changing between our checkout
 * and Stripe's wallet sheet (Apple Pay, Google Pay).
 *
 * The wallet collects the email, the name and the address, and shows its own
 * total. Everything the sheet shows comes from here, and so does everything
 * sent to the server afterwards, so the figure a buyer approves in their wallet
 * is the same figure the card form would have shown them for the same address
 * and rate. Pure: no fetches, no Stripe objects. The component does the I/O.
 */
import { checkoutPriceBreakdown, type CodeCouponState } from './price-breakdown';
import type { ShippingRate } from './checkout-types';
import { transitDays } from './delivery-estimate';

/**
 * What an express payment may be paid with: a card (which is what Apple Pay and
 * Google Pay hand over) or Link.
 *
 * The page builds its Elements group with this list before any intent exists,
 * and the server creates the intent with it; Stripe refuses a confirm where the
 * two disagree, after the buyer has already approved. So both read it here.
 *
 * Link is safe here where it is not in the card form. The card form's trap was
 * Link's signup opening INSIDE the Payment Element, asking for a phone the
 * buyer could not see, and leaving Pay dead (utils/checkout/payment-method-types.ts).
 * The Link button opens Stripe's own window, where the phone, if asked, is the
 * thing on screen.
 */
export const EXPRESS_PAYMENT_METHOD_TYPES: string[] = ['card', 'link'];

/** Only the US: express quotes one address at a time and cross-border shipping is arranged by hand. */
export const EXPRESS_SHIPPING_COUNTRIES = ['US'] as const;

/**
 * The rate the sheet opens with, before it has told us where it ships.
 *
 * The click handler has to answer within a second and cannot quote, but Stripe
 * wants a rate list whenever an address is asked for. Both wallets send the
 * address as soon as the sheet opens, and the real rates replace this one.
 * A confirm that still carries it is refused.
 */
export const EXPRESS_PENDING_RATE_ID = 'quote-pending';

/**
 * The single rate offered when the seller and buyer arrange shipping between
 * them (a seller with no shipping origin). It costs nothing, and the order goes
 * to the server with no rate at all, which is how that case is charged today.
 */
export const EXPRESS_ARRANGED_RATE_ID = 'arranged-separately';

/** Stripe's shapes, restated so this module needs no Stripe import. */
export interface ExpressSheetRate {
  id: string;
  amount: number;
  displayName: string;
  deliveryEstimate?: {
    minimum?: { unit: 'business_day'; value: number };
    maximum?: { unit: 'business_day'; value: number };
  };
}

export interface ExpressLineItem {
  name: string;
  amount: number;
}

/** The partial address a wallet sends before the buyer approves (no street). */
export interface ExpressPartialAddress {
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

/** The whole address the wallet sends on approval. */
export interface ExpressFullAddress extends ExpressPartialAddress {
  line1: string;
  line2: string | null;
}

/** The guest address shape the rest of checkout uses. */
export interface ExpressGuestAddress {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface ExpressTotalsInput {
  /** The listing as the checkout holds it: `price`, plus any automatic coupon keys. */
  listing: unknown;
  /** The listing title, for the sheet's first line. */
  title: string;
  /** The rate being charged, or null for arranged-separately / pending. */
  rate: ShippingRate | null;
  coupon: CodeCouponState;
}

export interface ExpressTotals {
  /** What the sheet shows and what the intent must charge, in cents. */
  amountCents: number;
  /** Everything except shipping, which the sheet draws from the chosen rate. */
  lineItems: ExpressLineItem[];
}

/** Longest item name a wallet sheet shows without cutting it off mid-word. */
const MAX_LINE_NAME = 60;

function lineName(title: string): string {
  const clean = title.replace(/\s+/g, ' ').trim() || 'Your item';
  if (clean.length <= MAX_LINE_NAME) return clean;
  const cut = clean.slice(0, MAX_LINE_NAME - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 30 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

const cents = (dollars: number): number => Math.round(dollars * 100);

/**
 * The sheet's total and its lines, for one rate.
 *
 * Built from checkoutPriceBreakdown, the same function the order summary draws
 * from, so the two can never disagree. The lines always add up to the total
 * less shipping, which the sheet adds itself from the selected rate.
 */
export function expressTotals(input: ExpressTotalsInput): ExpressTotals {
  const breakdown = checkoutPriceBreakdown({
    listing: input.listing,
    isVehicleListing: false,
    shippingAmount: input.rate?.amount ?? 0,
    taxCents: input.rate?.tax?.taxCents ?? 0,
    coupon: input.coupon,
  });

  const itemCents = cents(breakdown.itemPrice);
  const shipCents = cents(breakdown.shipAmount);
  const feeCents = cents(breakdown.stripeFee);
  const taxCents = cents(breakdown.salesTax);

  const lineItems: ExpressLineItem[] = [{ name: lineName(input.title), amount: itemCents }];
  // Never 0: the processing charge carries a fixed 30c on any amount.
  lineItems.push({ name: 'Processing', amount: feeCents });
  if (taxCents > 0) lineItems.push({ name: 'Sales tax', amount: taxCents });

  return { amountCents: itemCents + shipCents + feeCents + taxCents, lineItems };
}

/** "USPS Ground Advantage", not "USPS USPS Ground Advantage". */
function rateName(rate: ShippingRate): string {
  const provider = rate.provider?.name?.trim() || '';
  const service = rate.service?.trim() || '';
  if (!provider) return service || 'Shipping';
  if (!service || service.toUpperCase().startsWith(provider.toUpperCase())) return service || provider;
  return `${provider} ${service}`;
}

/**
 * Our quoted rates as the sheet lists them, cheapest first as quoted.
 *
 * The delivery window is the carrier's own range (delivery-estimate.ts), the
 * same one the checkout page shows. Shippo's flat `estimatedDays` gave every
 * USPS service the same "2 business days", Express included.
 */
export function toSheetRates(rates: ShippingRate[]): ExpressSheetRate[] {
  return rates.map((rate) => {
    const transit = transitDays(rate);
    const day = (value: number) => ({ unit: 'business_day' as const, value: Math.round(value) });
    return {
      id: rate.id,
      amount: cents(rate.amount),
      displayName: rateName(rate),
      ...(transit
        ? {
            deliveryEstimate: transit.min === transit.max
              ? { maximum: day(transit.max) }
              : { minimum: day(transit.min), maximum: day(transit.max) },
          }
        : {}),
    };
  });
}

export const PENDING_SHEET_RATE: ExpressSheetRate = {
  id: EXPRESS_PENDING_RATE_ID,
  amount: 0,
  displayName: 'Calculating shipping…',
};

export const ARRANGED_SHEET_RATE: ExpressSheetRate = {
  id: EXPRESS_ARRANGED_RATE_ID,
  amount: 0,
  displayName: 'Arranged with the seller',
};

/** The wallet's approved shipping address, as checkout stores a guest's. */
export function toGuestAddress(name: string, address: ExpressFullAddress): ExpressGuestAddress {
  return {
    name: name.replace(/\s+/g, ' ').trim(),
    line1: address.line1.trim(),
    line2: (address.line2 ?? '').trim(),
    city: address.city.trim(),
    state: address.state.trim(),
    postal_code: address.postal_code.trim(),
    country: (address.country || 'US').trim().toUpperCase(),
  };
}

/**
 * COUNTRY row ids by ISO code, as production holds them (and as the address
 * forms' hard-coded list in components/utils getCountries does).
 */
export const COUNTRY_ID_BY_ISO: Readonly<Record<string, number>> = { US: 237, CA: 41 };

/**
 * A signed-in buyer's wallet address, as a request to their address book
 * (POST /api/addresses). That route adopts an identical saved address rather
 * than adding a second, so paying twice to one address files it once. Never
 * made the default: a wallet purchase is not the buyer saying where they live.
 * Null for a country the address book does not take.
 */
export function toAddressBookRequest(address: ExpressGuestAddress): {
  shippingName: string;
  fullName: string;
  line1: string;
  line2: string;
  locality: string;
  region: string;
  zipCode: string;
  countryId: number;
  isDefault: 0;
} | null {
  const countryId = COUNTRY_ID_BY_ISO[address.country];
  if (!countryId) return null;
  return {
    shippingName: address.name,
    fullName: address.name,
    line1: address.line1,
    line2: address.line2,
    locality: address.city,
    region: address.state,
    zipCode: address.postal_code,
    countryId,
    isDefault: 0,
  };
}

/** What the quote route needs from a partial wallet address. */
export function toQuoteAddress(address: ExpressPartialAddress): Omit<ExpressGuestAddress, 'name' | 'line1' | 'line2'> {
  return {
    city: address.city.trim(),
    state: address.state.trim(),
    postal_code: address.postal_code.trim(),
    country: (address.country || 'US').trim().toUpperCase(),
  };
}

/**
 * The same service, quoted again for the whole address.
 *
 * The sheet quotes from a ZIP-level address, because that is all a wallet
 * reveals before approval. The label is later bought from the shipment the
 * CHARGED rate belongs to, so that shipment must carry the street -- the
 * approved address is quoted once more and the same carrier and service picked
 * from it. Matched on the service token first, the name second.
 */
export function matchRequotedRate(requoted: ShippingRate[], chosen: ShippingRate): ShippingRate | null {
  const sameProvider = (r: ShippingRate) =>
    (r.provider?.name || '').toUpperCase() === (chosen.provider?.name || '').toUpperCase();
  if (chosen.serviceToken) {
    const byToken = requoted.find((r) => sameProvider(r) && r.serviceToken === chosen.serviceToken);
    if (byToken) return byToken;
  }
  return requoted.find((r) => sameProvider(r) && r.service === chosen.service) ?? null;
}

/**
 * Whether a listing can be bought from the wallet buttons at all.
 *
 * A vehicle or a local-pickup listing has no rate to put on the sheet, and a
 * seller who cannot be paid out gets refused by the server after the buyer has
 * approved the payment -- so none of them get the buttons.
 */
export function canOfferExpress(listing: {
  price?: number | string | null;
  shippable?: boolean | number | string | null;
  isVehicle: boolean;
  sellerCanBePaid: boolean;
} | null): boolean {
  if (!listing) return false;
  const price = Number(listing.price);
  const shippable = listing.shippable === true || listing.shippable === 1 || listing.shippable === '1';
  return shippable && !listing.isVehicle && listing.sellerCanBePaid && Number.isFinite(price) && price > 0;
}

/** The wallets a buyer can actually use on this device, for the funnel. */
export function availableWallets(methods: Record<string, boolean | undefined> | undefined | null): string[] {
  if (!methods) return [];
  return Object.entries(methods)
    .filter(([, on]) => on === true)
    .map(([name]) => name)
    .sort();
}
